import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { intelligenceApi } from '@/services/api';
import {
  Brain, Settings2, Bug, ChevronDown, ChevronRight,
  Save, RotateCcw, Trash2, Eye, EyeOff, Clock, Zap,
} from 'lucide-react';

// Layer display metadata
const LAYER_META = {
  perceive:          { label: 'Perceive',          phase: 'Ingest',    color: 'text-blue-400   border-blue-500/30   bg-blue-500/5' },
  contextualize:     { label: 'Contextualize',     phase: 'Ingest',    color: 'text-cyan-400   border-cyan-500/30   bg-cyan-500/5' },
  handle_image:      { label: 'Handle Image',      phase: 'Ingest',    color: 'text-purple-400 border-purple-500/30 bg-purple-500/5' },
  think:             { label: 'Think',             phase: 'Heartbeat', color: 'text-violet-400 border-violet-500/30 bg-violet-500/5' },
  plan_query:        { label: 'Plan Query',        phase: 'Query',     color: 'text-amber-400  border-amber-500/30  bg-amber-500/5' },
  synthesize_answer: { label: 'Synthesize Answer', phase: 'Query',     color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5' },
};

export default function IntelligenceTab({ appId }) {
  const [layers, setLayers] = useState([]);
  const [debugSettings, setDebugSettings] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugStats, setDebugStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLayer, setExpandedLayer] = useState(null);
  const [editingLayer, setEditingLayer] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('layers'); // layers | debug

  // Fetch all intelligence data
  const fetchData = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError(null);

    try {
      const [layersRes, settingsRes, logsRes] = await Promise.all([
        intelligenceApi.getLayers(appId),
        intelligenceApi.getDebugSettings(appId),
        intelligenceApi.getDebugLogs(appId, { limit: 20 }),
      ]);

      setLayers(layersRes.data?.data?.layers || []);
      setDebugSettings(settingsRes.data?.data || { enabled: false, layers: [], retention_hours: 24 });
      setDebugLogs(logsRes.data?.data?.logs || []);
      setDebugStats(logsRes.data?.data?.stats || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Start editing a layer
  const startEdit = (layer) => {
    setEditingLayer(layer.layerId);
    setEditForm({
      model_config: { ...layer.model_config },
      prompt_config: { ...layer.prompt_config },
      enabled: layer.enabled,
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingLayer(null);
    setEditForm(null);
  };

  // Save layer changes
  const saveLayer = async (layerId) => {
    if (!editForm) return;
    setSaving(true);

    try {
      await intelligenceApi.updateLayer(appId, layerId, editForm);
      await fetchData();
      setEditingLayer(null);
      setEditForm(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle debug
  const toggleDebug = async () => {
    try {
      const updated = await intelligenceApi.updateDebugSettings(appId, {
        enabled: !debugSettings.enabled,
      });
      setDebugSettings(updated.data?.data || updated.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Purge debug logs
  const handlePurge = async () => {
    try {
      await intelligenceApi.purgeDebugLogs(appId);
      setDebugLogs([]);
      setDebugStats({ total: 0, byLayer: {} });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-2">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Section toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setActiveSection('layers')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            activeSection === 'layers'
              ? 'bg-primary/10 text-primary border border-primary/20'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Settings2 className="h-3.5 w-3.5" />
          Layers ({layers.length})
        </button>
        <button
          onClick={() => setActiveSection('debug')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            activeSection === 'debug'
              ? 'bg-primary/10 text-primary border border-primary/20'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Bug className="h-3.5 w-3.5" />
          Debug {debugSettings?.enabled && <span className="text-emerald-400 ml-0.5">ON</span>}
        </button>
      </div>

      {/* LAYERS SECTION */}
      {activeSection === 'layers' && (
        <div className="space-y-3">
          {/* Group by phase */}
          {['Ingest', 'Heartbeat', 'Query'].map((phase) => {
            const phaseLayers = layers.filter(
              (l) => LAYER_META[l.layerId]?.phase === phase
            );
            if (phaseLayers.length === 0) return null;

            return (
              <div key={phase}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {phase}
                </h3>
                <div className="space-y-2">
                  {phaseLayers.map((layer) => (
                    <LayerCard
                      key={layer.layerId}
                      layer={layer}
                      meta={LAYER_META[layer.layerId]}
                      isExpanded={expandedLayer === layer.layerId}
                      isEditing={editingLayer === layer.layerId}
                      editForm={editingLayer === layer.layerId ? editForm : null}
                      saving={saving}
                      onToggle={() =>
                        setExpandedLayer(expandedLayer === layer.layerId ? null : layer.layerId)
                      }
                      onEdit={() => startEdit(layer)}
                      onCancel={cancelEdit}
                      onSave={() => saveLayer(layer.layerId)}
                      onFormChange={setEditForm}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DEBUG SECTION */}
      {activeSection === 'debug' && (
        <div className="space-y-4">
          {/* Debug controls */}
          <div className="flex items-center justify-between glass rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleDebug}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors border',
                  debugSettings?.enabled
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                    : 'bg-muted/50 text-muted-foreground border-border'
                )}
              >
                {debugSettings?.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                {debugSettings?.enabled ? 'Debug ON' : 'Debug OFF'}
              </button>
              <span className="text-xs text-muted-foreground">
                <Clock className="h-3 w-3 inline mr-1" />
                Retention: {debugSettings?.retention_hours || 24}h
              </span>
            </div>

            {debugLogs.length > 0 && (
              <button
                onClick={handlePurge}
                className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Purge
              </button>
            )}
          </div>

          {/* Stats */}
          {debugStats && debugStats.total > 0 && (
            <div className="flex gap-2 flex-wrap">
              {Object.entries(debugStats.byLayer || {}).map(([layerId, count]) => {
                if (count === 0) return null;
                const meta = LAYER_META[layerId];
                return (
                  <span key={layerId} className={cn('text-xs px-2 py-0.5 rounded border', meta?.color || 'text-muted-foreground')}>
                    {meta?.label || layerId}: {count}
                  </span>
                );
              })}
              <span className="text-xs text-muted-foreground px-2 py-0.5">
                Total: {debugStats.total}
              </span>
            </div>
          )}

          {/* Debug log list */}
          {debugLogs.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              {debugSettings?.enabled
                ? 'No debug logs yet. Logs will appear after the next LLM call.'
                : 'Enable debug logging to capture LLM request/response payloads.'}
            </div>
          ) : (
            <div className="space-y-2">
              {debugLogs.map((log, i) => (
                <DebugLogCard key={log.sk || i} log={log} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// LAYER CARD
// ==========================================

function LayerCard({
  layer, meta, isExpanded, isEditing, editForm, saving,
  onToggle, onEdit, onCancel, onSave, onFormChange,
}) {
  const m = meta || { label: layer.layerId, color: 'text-muted-foreground' };

  return (
    <div className={cn('rounded-lg border transition-colors', m.color)}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div>
            <span className="text-sm font-medium">{m.label}</span>
            <span className="text-xs text-muted-foreground ml-2">
              {layer.model_config?.provider}/{layer.model_config?.model}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            temp {layer.model_config?.temperature} · {layer.model_config?.max_tokens} tokens
          </span>
          <span className={cn(
            'text-xs px-1.5 py-0.5 rounded',
            layer.enabled ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          )}>
            {layer.enabled ? 'ON' : 'OFF'}
          </span>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-inherit px-4 py-3 space-y-4">
          {isEditing && editForm ? (
            /* Edit mode */
            <div className="space-y-4">
              {/* Model config */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Model</label>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Provider</label>
                    <select
                      value={editForm.model_config.provider}
                      onChange={(e) =>
                        onFormChange({
                          ...editForm,
                          model_config: { ...editForm.model_config, provider: e.target.value },
                        })
                      }
                      className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded"
                    >
                      <option value="anthropic">Anthropic</option>
                      <option value="google">Google</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Model</label>
                    <input
                      type="text"
                      value={editForm.model_config.model}
                      onChange={(e) =>
                        onFormChange({
                          ...editForm,
                          model_config: { ...editForm.model_config, model: e.target.value },
                        })
                      }
                      className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Temperature</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={editForm.model_config.temperature}
                      onChange={(e) =>
                        onFormChange({
                          ...editForm,
                          model_config: { ...editForm.model_config, temperature: parseFloat(e.target.value) },
                        })
                      }
                      className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Max Tokens</label>
                    <input
                      type="number"
                      step="256"
                      min="128"
                      max="16384"
                      value={editForm.model_config.max_tokens}
                      onChange={(e) =>
                        onFormChange({
                          ...editForm,
                          model_config: { ...editForm.model_config, max_tokens: parseInt(e.target.value) },
                        })
                      }
                      className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Prompt config */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Prompt</label>
                {['role_instructions', 'task_instructions', 'guidelines'].map((field) => (
                  <div key={field}>
                    <label className="text-xs text-muted-foreground capitalize">
                      {field.replace('_', ' ')}
                    </label>
                    <textarea
                      value={editForm.prompt_config[field] || ''}
                      onChange={(e) =>
                        onFormChange({
                          ...editForm,
                          prompt_config: { ...editForm.prompt_config, [field]: e.target.value },
                        })
                      }
                      rows={4}
                      className="w-full mt-0.5 px-2 py-1.5 text-xs bg-background border border-border rounded font-mono resize-y"
                    />
                  </div>
                ))}
              </div>

              {/* Enabled toggle */}
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={editForm.enabled}
                  onChange={(e) => onFormChange({ ...editForm, enabled: e.target.checked })}
                  className="rounded border-border"
                />
                Enabled
              </label>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50"
                >
                  <Save className="h-3.5 w-3.5" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={onCancel}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted/50 transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* View mode */
            <div className="space-y-3">
              {/* Prompt preview */}
              {layer.prompt_config?.role_instructions && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Role</label>
                  <p className="text-xs mt-0.5 text-foreground/70 line-clamp-3 font-mono">
                    {layer.prompt_config.role_instructions}
                  </p>
                </div>
              )}
              {layer.prompt_config?.task_instructions && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Task</label>
                  <p className="text-xs mt-0.5 text-foreground/70 line-clamp-3 font-mono">
                    {layer.prompt_config.task_instructions}
                  </p>
                </div>
              )}
              {layer.prompt_config?.guidelines && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Guidelines</label>
                  <p className="text-xs mt-0.5 text-foreground/70 line-clamp-2 font-mono">
                    {layer.prompt_config.guidelines}
                  </p>
                </div>
              )}

              <button
                onClick={onEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted/50 transition-colors"
              >
                <Settings2 className="h-3.5 w-3.5" />
                Edit Configuration
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// DEBUG LOG CARD
// ==========================================

function DebugLogCard({ log }) {
  const [expanded, setExpanded] = useState(false);
  const meta = LAYER_META[log.layerId];

  const timestamp = new Date(log.sk || log.created_at);
  const timeAgo = getRelativeTime(timestamp);

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-3 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          <span className={cn('text-xs px-1.5 py-0.5 rounded border', meta?.color || 'text-muted-foreground')}>
            {meta?.label || log.layerId}
          </span>
          <span className="text-xs text-muted-foreground">
            {log.model} · {log.latency_ms}ms
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            <Zap className="h-3 w-3 inline mr-0.5" />
            {log.input_tokens}→{log.output_tokens}
          </span>
          <span>{timeAgo}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border/50 px-3 py-2 space-y-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">System Prompt</label>
            <pre className="text-xs mt-0.5 text-foreground/70 font-mono whitespace-pre-wrap max-h-32 overflow-y-auto code-block p-2 rounded">
              {log.system_prompt || '(none)'}
            </pre>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">User Prompt</label>
            <pre className="text-xs mt-0.5 text-foreground/70 font-mono whitespace-pre-wrap max-h-32 overflow-y-auto code-block p-2 rounded">
              {log.user_prompt || '(none)'}
            </pre>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Response</label>
            <pre className="text-xs mt-0.5 text-foreground/70 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto code-block p-2 rounded">
              {log.response_parsed || log.response_raw || '(empty)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// HELPERS
// ==========================================

function getRelativeTime(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString();
}
